import React, { useEffect, useRef, useState } from "react";

const CANVAS_W = 420;
const CANVAS_H = 260;
const COLORS = ["#111827", "#0d6efd", "#dc3545", "#198754"];

/**
 * Floating, draggable scratch whiteboard.
 *
 * - `open`     : show / hide the panel. The canvas stays mounted while hidden,
 *                so whatever was drawn is still there when it is reopened.
 * - `onClose`  : called by the X button.
 * - `resetKey` : whenever this value changes the board is wiped
 *                (Examination passes the current EA section number).
 */
export default function Whiteboard({ open, onClose, resetKey }) {
  const canvasRef = useRef(null);
  const panelRef = useRef(null);
  const drawingRef = useRef(false);

  const [tool, setTool] = useState("pen"); // "pen" | "eraser"
  const [color, setColor] = useState(COLORS[0]);
  const [pos, setPos] = useState(null); // { x, y } once the panel has been dragged

  const clearBoard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  // White background on mount, and a fresh board whenever resetKey changes.
  useEffect(() => {
    clearBoard();
  }, [resetKey]);

  /* ---------- drawing ---------- */
  const pointFromEvent = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) * canvas.width) / rect.width,
      y: ((e.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const handlePointerDown = (e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    canvas.setPointerCapture(e.pointerId);
    drawingRef.current = true;

    const ctx = canvas.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = tool === "eraser" ? 16 : 2.5;
    ctx.strokeStyle = tool === "eraser" ? "#ffffff" : color;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // a tiny segment so a single tap leaves a dot
    ctx.lineTo(x + 0.01, y + 0.01);
    ctx.stroke();
  };

  const handlePointerMove = (e) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pointFromEvent(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
  };

  /* ---------- dragging the panel by its header ---------- */
  const startDrag = (e) => {
    if (e.target.closest("button")) return;
    const rect = panelRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;

    const move = (ev) => {
      setPos({
        x: Math.min(Math.max(0, ev.clientX - offsetX), window.innerWidth - rect.width),
        y: Math.min(Math.max(0, ev.clientY - offsetY), window.innerHeight - rect.height),
      });
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const positionStyle = pos ? { left: pos.x, top: pos.y } : { right: 24, bottom: 90 };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Whiteboard"
      style={{
        position: "fixed",
        ...positionStyle,
        display: open ? "block" : "none",
        width: CANVAS_W + 24,
        maxWidth: "92vw",
        zIndex: 1050,
        background: "#fff",
        border: "1px solid #dfe3ea",
        borderRadius: 12,
        boxShadow: "0 12px 30px rgba(15, 23, 42, 0.25)",
        overflow: "hidden",
      }}
    >
      {/* Header (drag handle) */}
      <div
        onPointerDown={startDrag}
        className="d-flex align-items-center justify-content-between px-3 py-2"
        style={{ background: "#12233f", color: "#fff", cursor: "move", userSelect: "none" }}
      >
        <span className="small fw-semibold">
          <i className="bi bi-easel2 me-2" />
          Whiteboard
        </span>
        <button
          type="button"
          className="btn btn-sm p-0 text-white"
          aria-label="Close whiteboard"
          onClick={onClose}
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>

      {/* Tools */}
      <div className="d-flex align-items-center flex-wrap gap-2 px-3 py-2 border-bottom">
        <div className="btn-group btn-group-sm" role="group" aria-label="Tool">
          <button
            type="button"
            className={`btn ${tool === "pen" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTool("pen")}
          >
            <i className="bi bi-pencil" /> Pen
          </button>
          <button
            type="button"
            className={`btn ${tool === "eraser" ? "btn-primary" : "btn-outline-primary"}`}
            onClick={() => setTool("eraser")}
          >
            <i className="bi bi-eraser" /> Eraser
          </button>
        </div>

        <div className="d-flex align-items-center gap-1">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={`Pen colour ${c}`}
              onClick={() => {
                setColor(c);
                setTool("pen");
              }}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: c,
                border: color === c && tool === "pen" ? "2px solid #0d6efd" : "2px solid #fff",
                boxShadow: "0 0 0 1px #cbd2dc",
                padding: 0,
              }}
            />
          ))}
        </div>

        <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={clearBoard}>
          <i className="bi bi-trash3" /> Clear
        </button>
      </div>

      {/* Drawing surface */}
      <div className="p-2">
        <canvas
          ref={canvasRef}
          width={CANVAS_W}
          height={CANVAS_H}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onPointerLeave={stopDrawing}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            border: "1px solid #e5e9f0",
            borderRadius: 8,
            background: "#fff",
            touchAction: "none",
            cursor: tool === "eraser" ? "cell" : "crosshair",
          }}
        />
      </div>
    </div>
  );
}